import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';

const Section = styled.section`
  padding: 10rem 0;
  text-align: center;
  position: relative;
`;

const Container = styled.div`
  width: 100%;
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 2rem;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 0 1.25rem;
  }
`;

const Label = styled(motion.p)`
  font-family: 'JetBrains Mono', monospace;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1.5rem;
  font-size: 1rem;
`;

const Title = styled(motion.h2)`
  font-size: clamp(2.5rem, 5vw, 4rem);
  color: ${props => props.theme.colors.text};
  margin-bottom: 2rem;
  font-weight: 700;
  letter-spacing: -0.03em;
`;

const Description = styled(motion.p)`
  font-size: 1.2rem;
  color: ${props => props.theme.colors.textDim};
  line-height: 1.9;
  margin-bottom: 3.5rem;
`;

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(56, 189, 248, 0.4); }
  70% { box-shadow: 0 0 0 20px rgba(56, 189, 248, 0); }
  100% { box-shadow: 0 0 0 0 rgba(56, 189, 248, 0); }
`;

const Button = styled(motion.a)`
  display: inline-block;
  padding: 1.25rem 3.5rem;
  font-size: 1.1rem;
  font-weight: 600;
  color: ${props => props.theme.colors.bg};
  background: ${props => props.theme.colors.primary};
  border-radius: 8px;
  border: 2px solid ${props => props.theme.colors.primary};
  font-family: 'JetBrains Mono', monospace;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.3),
      transparent
    );
    transition: left 0.5s;
  }
  
  &:hover {
    background: transparent;
    color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 30px rgba(56, 189, 248, 0.3);
    transform: translateY(-3px);
    
    &::before {
      left: 100%;
    }
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const Divider = styled(motion.div)`
  width: 60px;
  height: 4px;
  background: linear-gradient(90deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.accent});
  margin: 0 auto 2rem;
  border-radius: 2px;
`;

const GalleryContainer = styled(motion.div)`
  margin-top: 4rem;
  width: 50%;
  max-width: 500px;
  aspect-ratio: 1.4;
  margin-left: auto;
  margin-right: auto;
  position: relative;
  
  /* 부드러운 페이드 마스크 */
  mask-image: linear-gradient(to bottom, black 80%, transparent 100%);

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    width: 90%;
    margin-top: 3rem;
  }
`;

const GalleryImage = styled(motion.img)`
  width: 100%;
  height: 100%;
  object-fit: contain;
  position: absolute;
  top: 0;
  left: 0;
  filter: drop-shadow(0 10px 20px rgba(0,0,0,0.3));
  
  /* 이미지가 너무 밝으면 다크모드에서 눈부시므로 약간 조절 */
  opacity: 0.9;
  transition: opacity 0.3s;
  
  &:hover {
    opacity: 1;
  }
`;

const AwardsGallery = () => {
  const images = [
    "/screenshots/16.jpg",
    "/screenshots/17.jpg",
    "/screenshots/18.jpg"
  ];
  
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % images.length);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  return (
    <GalleryContainer
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true }}
      transition={{ delay: 0.5, duration: 1 }}
    >
      <AnimatePresence mode="wait">
        <GalleryImage
          key={index}
          src={images[index]}
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 1.05 }}
          transition={{ duration: 0.8, ease: "easeInOut" }}
          alt="Award Certificate"
        />
      </AnimatePresence>
    </GalleryContainer>
  );
};

const Contact = () => {
  return (
    <Section id="contact">
      <Container>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <Label>05. Contact</Label>
          <Title>Reason for leaving</Title>
          <Divider 
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          />
          <Description>
            쿠팡이츠에서 여러 부서의 비즈니스 로직에 맞춘 자동화 개선으로 지표에 미치는 영향력을 경험했습니다.<br />
            특정 서비스에 머물러 있는 것이 아니라 다양한 도메인의 문제를 확인하고 자동화를 설계하며<br />
            배움과 성장의 목표로 전문가로 나아가고자 합니다.
          </Description>
          <Button 
            href="https://mail.google.com/mail/?view=cm&fs=1&to=checkdata.yul@gmail.com"
            target="_blank"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            contact me
          </Button>
          
          <AwardsGallery />
        </motion.div>
      </Container>
    </Section>
  );
};

export default Contact;
